import { shouldShowQuoteItem } from "@/shared/components/QuoteList/quoteDisplay";

type QuoteSectionItem = {
  label?: string;
  section_name?: string;
};

type QuoteSection = {
  title?: string;
  items?: QuoteSectionItem[];
};

const ADD_ON_SECTION_TITLES = new Set(["add-ons", "add ons", "addons"]);
const GENERIC_SECTION_TITLES = new Set(["section 1", "section 2"]);
const PREFERRED_SECTION_ORDER = ["Basic Features", "Additional Coverages"];

const normalizeText = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const normalizeKey = (value: unknown) => normalizeText(value).toLowerCase();

const isAddOnSectionTitle = (title: unknown) => ADD_ON_SECTION_TITLES.has(normalizeKey(title));

const isGenericSectionTitle = (title: unknown) => GENERIC_SECTION_TITLES.has(normalizeKey(title));

export function getNormalizedQuoteSections(
  sections: any[] = [],
  options: {
    excludeLabels?: string[];
    filterItems?: (item: any) => boolean;
  } = {}
) {
  const sectionMap = new Map<string, QuoteSection>();
  const excludeLabelKeys = new Set(
    (options.excludeLabels || []).map((label) => normalizeKey(label))
  );
  const shouldKeepItem = options.filterItems || shouldShowQuoteItem;

  sections.forEach((section) => {
    if (isAddOnSectionTitle(section?.title)) {
      return;
    }

    (section?.items || []).forEach((item) => {
      const titleFromItem = normalizeText(item?.section_name);
      const sectionTitle = titleFromItem || normalizeText(section?.title);

      if (!sectionTitle || isGenericSectionTitle(sectionTitle)) {
        return;
      }

      if (item?.label && excludeLabelKeys.has(normalizeKey(item.label))) {
        return;
      }

      if (!shouldKeepItem(item)) {
        return;
      }

      if (!sectionMap.has(sectionTitle)) {
        sectionMap.set(sectionTitle, {
          ...section,
          title: sectionTitle,
          items: [],
        });
      }

      sectionMap.get(sectionTitle)?.items?.push(item);
    });
  });

  return Array.from(sectionMap.values()).sort((a, b) => {
    const aOrder = PREFERRED_SECTION_ORDER.indexOf(a.title || "");
    const bOrder = PREFERRED_SECTION_ORDER.indexOf(b.title || "");

    if (aOrder !== -1 || bOrder !== -1) {
      return (
        (aOrder === -1 ? Number.MAX_SAFE_INTEGER : aOrder) -
        (bOrder === -1 ? Number.MAX_SAFE_INTEGER : bOrder)
      );
    }

    return String(a.title || "").localeCompare(String(b.title || ""));
  });
}
